/**
 * PrivacyPolicyPage - מדיניות פרטיות
 *
 * SOURCE OF TRUTH: "מדיניות פרטיות - יפתח ונגר - יולי 2026.docx" — the version
 * drafted and approved by counsel. The text below is a verbatim transcription
 * including the section/clause numbering (1 / 1.1 / 1.1.1); do NOT reword it.
 * Any change must come from an updated signed document.
 *
 * ONE DELIBERATE DEVIATION: the service address in the opening paragraph is the
 * live one (SITE_URL). The signed .docx names an older domain that no longer
 * serves the app — publishing an address that doesn't resolve would be worse
 * than deviating. Have counsel correct it in the next revision.
 *
 * Published per חוק הגנת הפרטיות, התשמ"א-1981 (לרבות תיקון 13) ותקנות הגנת
 * הפרטיות (שימוש במידע אישי ומסירתו), התשפ"ה-2025.
 * Public route: /privacy — linked from registration, the footer, and the
 * post-signing legal consent modal.
 */

import { Link } from 'react-router-dom'
import LegalDocument, { LegalContact, SITE_URL, type Section } from '../components/legal/LegalDocument'

const PRIVACY_PHONE = '055-9859785'
const PRIVACY_EMAIL = 'iftach.venger@gmail.com'
const LAST_UPDATED = 'יולי 2026'

const PREAMBLE = [
  <>
    יפתח ונגר מ.ע 034025197 ("החברה" ו/או "מפעיל האתר") מברך את בחירתכם לגלוש באתר האינטרנט ו/או
    במערכת המופעלים על ידו בכתובת: <span dir="ltr">{SITE_URL}</span> ("המערכת"/"האתר").
  </>,
  'מפעיל האתר פועל בהתאם להוראות חוק הגנת הפרטיות, התשמ"א–1981, לרבות תיקון 13 לחוק ולדרישות תקנות הגנת הפרטיות (שימוש במידע אישי ומסירתו), התשפ"ה- 2025 ("החוק").',
  'באתר תוכלו למצוא מידע רב אודות השירותים השונים המוצעים באתר, לרבות ניהול צ\'אט AI, אפשרות לקביעת פגישות, השארת פרטים ליצירת קשר עם האתר ועוד.',
  'מדיניות הפרטיות להלן מהווה חלק בלתי נפרד מתקנון ותנאי השימוש של האתר ומכסה את האופן שבו אנו משתמשים, מטפלים, מעבדים ושומרים את המידע שאנו אוספים.',
  'מדיניות פרטיות זו תחול על כל מידע שיימסר על ידך במסגרת פעילות האתר ו/או בכל מקרה בו תפנה לקבלת שירות ובכל מקרה בו תשאיר פרטים באמצעות דף זה או בכל מקרה בו תשאיר לנו פרטים אודותיך בכל אחד מאמצעי ההתקשרות איתנו, לרבות דוא"ל, טלפון, וואטסאפ, מסרון וכו\'.',
  'האתר מכבד את פרטיות הלקוחות והמשתמשים באתר, ורואה חשיבות רבה בשמירה עליה. השימוש באתר ובשירותים המוצעים מהווה הסכמה למדיניות פרטיות זו.',
]

const SECTIONS: Section[] = [
  {
    title: 'הסכמה לאיסוף המידע באתר:',
    clauses: [
      {
        text: 'בעת השארת פרטים באתר לרבות לצרכי רכישת קבלת שירותים ו/או הרשמה לאתר ו/או שימוש בערכות האתר, יצירת קשר ו/או כל מטרה אחרת, יתבקש המשתמש למסור פרטים ומידע מסוים, המידע אותו הנך מוסר בעת הרשמתך ו/או שימושך באתר נאסף ונשמר במאגרי המידע של האתר/החברה ויעובד, בהתאם למפורט בתנאי מדיניות זו.',
      },
      {
        text: 'מובהר כי אין חובה על-פי חוק למסור את המידע, אולם בלא למוסרו לא ניתן יהיה להשתמש בשירותי באתר ו/או ליצור קשר עם נציגי האתר.',
      },
      { text: 'אם אינך מסכים למדיניות פרטיות זו, אתה מתבקש לא לעשות שימוש באתר.' },
      {
        text: 'אתה מסכים, מאשר ומצהיר כי ידוע לך וכי אתה מבין שהשארת המידע ושהשימוש באתר ובמערכותיו כפוף לתנאי תקנון זה, חתימתך על הסכם ייעוץ השקעות באתר ("הסכם ייעוץ השקעות") והדין הרלוונטי לרבות אך לא רק חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995, חוק ניירות ערך, התשכ"ח-1968 וחוק איסור הלבנת הון, התש"ס-2000 לרבות שמירת המידע על ידי מפעיל האתר כמתחייב בחוק.',
      },
    ],
  },
  {
    title: 'המידע הנאסף באתר:',
    intro: ['המידע הנאסף על ידי האתר/החברה הינו:'],
    clauses: [
      {
        text: 'מידע אישי מזהה: שם מלא, כתובת דוא"ל, תאריך לידה, מספר זהות, כתובת, מספר טלפון וכיו"ב.',
      },
      {
        text: 'מידע פיננסי/כלכלי: שווי אחזקות, הכנסות, התחייבויות, נכסים, משכורות, הוצאות, ביטוחים, השקעות, קופות גמל למיניהן, שאלון הערכת סיכונים הכולל שאלות בדבר פעולותיו הפיננסיות של המשתמש, יחסו לסיכון, ניסיון בשוק ההון, מטרות השקעה וכיו"ב.',
        notes: ['על המשתמש לעדכן את האתר ו/או המערכת מיד עם שינוי בפרטיו ולעדכן את פרופיל הסיכון.'],
      },
      {
        text: 'מידע באמצעות צ\'אט: כאשר משתמשים מתקשרים עם הצ\'אט באתר, נאסף מידע על תוכן השיחה, זמן הפנייה ופרטי המשתמש.',
      },
      {
        text: 'מידע טכני ואיסוף אוטומטי: כתובות IP, סוג דפדפן, מערכת הפעלה, רזולוציית מסך סוג מכשיר, נתוני התחברות, נתוני "Cookies", פעולות באתר ודפי אתר נצפים, משך הזמן בכל דף, לחיצות על כפתורים / לינקים / טפסים, נתיב הניווט וגלילה, וחיפושים שבוצעו באתר זמני גלישה וכניסה לאתר, אופן הגלישה והכלי בו הנך משתמש למטרת הגלישה, פרטים לגבי ספק האינטרנט שלך וכתובות אתרי האינטרנט מהם הגעת.',
      },
      {
        text: 'מידע נוסף: מידע כאמור או מידע אחר הנמסר על ידך באופן יזום במסגרת הרשמה/אספקת שירותים, עמודי מילוי הפרטים באתר, סקרים, משובים ו/או דירוג או פניות לשירות לקוחות כולל תוכן התכתבות ו/או שיחות מוקלטות ו/או מידע נוסף שיידרש למען אספקת השירותים או מידע שנקבל מצדדים שלישיים.',
        notes: [
          'המידע המבוקש יכול להשתנות ונתון לשיקול דעת מנהלי האתר ובהתאם להוראות חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995 וכל דין אחר הרלוונטי לצורך אספקת השירותים.',
        ],
      },
      {
        text: 'המידע הנמסר בעת ההרשמה והמידע הנאסף על ידי האתר אמור בסעיף 2 שלעיל יכונו יחד: "המידע הנאסף באתר".',
      },
    ],
  },
  {
    title: 'מטרות שימוש במידע הנאסף באתר:',
    intro: ['האתר ו/או מי מטעמו רשאים לעשות שימוש במידע הנאסף באתר למטרות הבאות:'],
    clauses: [
      {
        text: 'אספקת שירותים: בכדי לספק לך את המידע / השירות שביקשת, בין היתר:',
        sub: [
          'עיבוד הזמנות: הכנת מסמכים, טפסים, אישורים, אימות, ביצוע תשלום או זיכוי, תיאום פגישה, מתן מידע וכיו"ב;',
          'שליחת מידע תפעולי: אישורי הזמנה/פגישה, חשבוניות, קבלות או כל מסמך נלווה לשירות שהוזמן.',
          'ניהול צ\'אט AI: אספקת מידע באמצעות צ\'אט AI בהתאם לפרופיל הסיכון ולמידע והפרטים שהזנת בעת ההרשמה לאתר.',
        ],
      },
      {
        text: 'שירות הלקוחות ותמיכה: יצירת קשר, ווידוא הזמנה, מענה על שאלות, תיקון תקלות ובעיות, טיפול בפניות, לספק למשתמש נתונים בקשר לאתר או לשירות וכיו"ב.',
      },
      {
        text: 'ניתוח ושיפור השירות: על מנת להתאים את שירותי האתר להעדפות הלקוח, ייתכן ונבצע סקרי לקוחות, מחקרי משתמשים, מחקרי שיווק, ניתוח מידע, דירוגים וכיו"ב.',
      },
      {
        text: 'פרסומות ושיווק: נשתמש במידע בכדי להתאים פרסומות ומידע מסחרי על פי העדפותיך האישיות לרבות לספק ו/או להציע לך שירותים נוספים, מסרים, עדכונים ועוד.',
        notes: [
          'יובהר, כי פרסומות באמצעות דיוור ישיר הינה בכפוף לקבלת הסכמה הלקוח ומתן אפשרות להסרה, כנדרש על פי חוק.',
          'ניתן בכל עת לבטל את ההרשמה לקבלת דיוור ישיר ע"י לחיצה על מקש "הסר" בכל דיוור שישלח ו/או ע"י שליחת הודעה לנייד/דוא"ל אשר יופיע בהודעה או כאמור בהודעת הדיוור.',
          <>
            ראו הרחבה בקשר לדיוור ישיר ב
            <Link to="/terms" className="text-primary-700 underline hover:text-primary-800">
              תקנון האתר
            </Link>
            .
          </>,
        ],
      },
      { text: 'תפעולו התקין של האתר: תחזוקת האתר, אספקת נתונים, עדכון האתר.' },
      {
        text: 'עמידה בדרישות חוקיות ורגולטוריות: על מנת לעמוד בדרישת כל חוק ו/או תקנה לרבות דרישת כל רשות שלטונית ו/או כל פעולה שהאתר ו/או מי מטעמו נדרשים על פי חוק לבצע בין אם בעקבות אופי השירותים המסופקים על ידי האתר ובין אם בכלל.',
      },
      {
        text: 'שמירה ואיסוף מידע בהתאם להוראות חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995 וכל דין אחר הרלוונטי לצורך אספקת השירותים.',
        notes: ['לדוגמא: שמירת מסמכים, תיעוד עסקאות ופניות ואופן טיפולן.'],
      },
      {
        text: 'מניעת הונאות ואבטחת מידע: עיבוד, סקירה, דגימה וניהול של המידע הנאסף באתר על מנת לזהות ולמנוע הונאות ושימוש לרעה באתר, במידע ו/או בשירותים המסופקים ונמכרים על ידי האתר.',
      },
      {
        text: 'הליכים משפטיים והגנות: שימוש במידע הנאסף באתר על מנת להגן על זכויותיהם המשפטיות והקנייניות של האתר ו/או מי מטעמו בכל הנוגע לאתר זה ולשירותים המסופקים ו/או נמכרים על ידו;',
      },
    ],
  },
  {
    title: 'העברת המידע הנאסף באתר לצדדים שלישיים:',
    clauses: [
      {
        text: 'האתר ו/או מי מטעמו לא יגלו ו/או ימכרו כל מידע הנאסף באתר לכל צד שלישי כלשהו, וזאת למעט למי מטעמם ו/או נותני שירותים בהם התעניינת אשר להם הם מספקים את המידע הנאסף באתר ואך ורק לצורך מתן שירות ואשר אף הם מחויבים להגנה מלאה על סודיות המידע הנאסף באתר.',
      },
      {
        text: 'יחד עם זאת, האתר ו/או מי מטעמו יהיו רשאים להעביר מידע הנאסף באתר לצדדים שלישיים בכל אחד מהמקרים הבאים:',
        sub: [
          'ניתנה לכך הסכמתך מראש ובכתב;',
          'האתר ו/או מי מטעמו נדרשים על פי חוק להעביר את המידע האמור, למשל, מכוח צו בית משפט ו/או חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995, חוק ניירות ערך, התשכ"ח-1968 וחוק איסור הלבנת הון, התש"ס-2000;',
          'על מנת להגן על זכויותיהם המשפטיות והקנייניות של האתר ו/או מי מטעמו בכל הנוגע לאתר, הפעלתו ולשימוש בו;',
          'במקרה של מחלוקת משפטית בינך לבין האתר ו/או מי מטעמו אשר תחייב חשיפת הפרטים;',
          'אם תפר את הוראות מדיניות הפרטיות ו/או תקנון ותנאי השימוש באתר או אם תבצע באמצעות האתר או בקשר כלשהו עם האתר, פעולות מנוגדות או הנחזות כמנוגדות לדין, או כל ניסיון לבצע פעולות כאלה;',
          'בכל מקרה שהאתר יסבור, כי מסירת המידע הנאסף נחוצה על מנת למנוע נזק חמור לגוף המשתמש או לרכושו או לגופו או לרכושו של צד שלישי;',
          'אם האתר ימוזג לתוך פעילות גוף אחר או אם האתר יעבור לבעלות תאגיד אחר ניתן יהיה להעביר לתאגיד החדש את המידע הקיים באתר, אבל רק במקרה שהתאגיד יתחייב לשמור על מדיניות פרטיות זו.',
          'מידע רפואי יישמר ויאוחסן על פי חוק ולא יועבר לצדדים שלישיים אלא בהסכמת בעל המידע ו/או על פי היתר חוקי וכנדרש ואשר גם הוא מחויב לשמירה על המידע.',
          'לצבור מידע על לקוחות ו/או משתמשים ולשפר את שירות הלקוחות. לאחר מכן, האתר עשוי לשתף מידע כאמור עם שותפי שיווק פוטנציאליים ומפרסמים;',
        ],
      },
      {
        text: 'מבלי לגרוע מכלליות האמור לעיל, המידע הנאסף באתר עשויי להיות משותף, בין היתר, עם שותפים עסקיים, ספקי שירות המעניקים לאתר ו/או לחברה שירותים טכנולוגיים, שיווקיים, משפטיים או תפעוליים, וזאת רק בגין המידע הרלוונטי עבור אותו שירות, בין היתר:',
        sub: [
          'ספקי תוכנה, שירותי ענן, שרתי אחסון מידע, מערכות CRM והנהלת חשבונות;',
          'פלטפורמות מקוונות וספקי שירותים מקוונים כגון: רב מסר, וורדפרס WordPress',
          'נותני שירות לוגיסטי כגון: חברות משלוחים, אחסנה, שרתים וכיו"ב;',
          'ספקי פרסום דיגיטלי (Google, Meta, Taboola, Pixel, AdSense, Doubleclick);',
          'רואה חשבון ו/או יועץ משכנתאות ו/או יועץ השקעות המעניק שירותים לאתר ו/או לחברה ו/או למשתמשי האתר;',
          'מערכות סליקה, מערכות ניהול חשבוניות, קופות ואשראי (כל צד כזה מחויב לשמירה על אבטחת המידע בהתאם לדין);',
          'מערכות ניהול דיוור מסרים ופרסומים;',
          'דיוור ישיר, פרסום וניהול קמפיינים באמצעות חברות צד שלישי משתנות;',
          'לצרכים סטטיסטים – אנו מספקים מידע אישי כגון המספר הכולל של המבקרים באתר זה ובכל עמוד של האתר וכן שמות הדומיין של נותני שירות האינטרנט של המבקרים באתר לגופים וחברות שאנו נותנים בהן אמון כדי שיעבדו את המידע עבורנו לפי הוראות האתר ובאופן העולה בקנה אחד עם תקנון האתר, מדיניות פרטיות זו ונהלי החברה. ככלל, וככל שלא ניתנה הסכמה מראש למסירת מידע אישי, מידע המועבר לצרכים סטטיסטים אינו כולל פרטים מזהים;',
          'לנותני שירותים ושותפים עסקיים כאמור ניתנת גישה לכל או לחלק מהמידע שלך, והם עשויים להשתמש ב"Cookies" או בטכנולוגיית איסוף אחרות.',
          'אחרים: רשויות אכיפת חוק, רשויות ממשלתיות ו/או גופים רגולטוריים.',
        ],
      },
      {
        text: 'המידע הנאסף באתר מאוחסן בשרתי האחסון שלנו העשויים להיות ממוקמים בישראל או מחוץ לישראל. אתה מאשר בזה שעם גלישתך באתר ו/או מסירת נתונים על ידך הדבר מהווה הסכמה שלך לכך שהמידע אותו תמסור יאוחסן על ידנו במקומות האחסון האמורים וזאת בכפוף לתנאי שמירת מידע רופאי ו/או מידע בעל רגישות מיוחדת.',
      },
      {
        text: 'לנותני שירותים ושותפים עסקיים כאמור ניתנת גישה לכל או לחלק מהמידע שלך, והם עשויים להשתמש ב"Cookies" או בטכנולוגיית איסוף אחרות.',
      },
    ],
  },
  {
    title: 'שימוש בקובצי Cookie וכלי מעקב',
    clauses: [
      {
        text: 'האתר עושה שימוש בקבצי Cookie או טכנולוגיות מעקב דומות ("Cookies" ו/או "עוגיות") זאת לצורך תפעול שוטף ותקין, אבטחת נתונים ומידע, איסוף נתונים סטטיסטיים אודות השימוש בשירותים, קבלת החלטות בנוגע לפרופיל ו/או פעילותך, אימות פרטים וזיהוי, מניעת הונאות ותרמיות, שיפור חווית השימוש, התאמת תכנים ושירותים להעדפותיך, אפיון מוצרים מתאימים, התאמת פרסומות ותכנים רלוונטיים, וכל זאת למטרות סטטיסטיות, מחקריות, שיווקיות, שירותיות ומסחריות, וכן לצרכי אבטחת מידע.',
      },
      {
        text: 'בין היתר, עשויות העוגיות לאפשר שמירת מידע אודותיך, מידע אודות משך השהייה שלך בדף, ניטור הרגלי גלישה ופעילות שלך, נתונים ופרטי ספק האינטרנט שלך, מידע על כתובת IP שלך, נתוני מיקום גיאוגרפי שלך ועוד.',
      },
      {
        text: 'חלק מהעוגיות יפוגו, עם סיום הגלישה וסגירת הדפדפן ואחרות ישמרו בזיכרון המחשב (או המכשיר) שבו השתמשת. אנו רוצים לעדכן אותך כי באמצעות שינוי הגדרות בדפדפן האינטרנט בו אתה משתמש, תוכל לקבוע ולהגדיר, אילו סוגים של "Cookies" יופעלו במהלך השימוש שלך, וכן תוכל לבחור למחוק, או להפסיק לחלוטין את השימוש בהן. חשוב להבהיר כי לחסימה של כל "העוגיות" עשויה להיות השפעה שלילית על נוחות השימוש שלך באתר ואף חסימת האפשרות לעשות שימוש באתר.',
        notes: [
          <>
            פירוט העוגיות שבשימוש בפועל מופיע ב
            <Link to="/cookies" className="text-primary-700 underline hover:text-primary-800">
              מדיניות העוגיות
            </Link>
            .
          </>,
        ],
      },
    ],
  },
  {
    title: 'שמירת ואבטחת מידע:',
    clauses: [
      {
        text: 'האתר נוקט צעדים מהותיים על מנת לסייע בהגנה על אבטחת הנתונים האישיים של המשתמשים והמידע הנאסף באתר. האתר עושה מאמצים על מנת לאסוף את המידע ולאבטחו בהתאם למדיניות הפרטיות ולחוקים והתקנות החלים במדינת ישראל.',
      },
      {
        text: 'לשם כך, האתר ו/או החברה מיישמים אמצעים טכנולוגיים וארגוניים לאבטחת המידע הנאסף באתר, בין היתר, אנטי-וירוס, אמצעי זיהוי ואימות, הצפנת מידע (ככל שנדרש), מנגנוני בקרה ומדיניות הרשאות תוך הגבלת גישה לא מורשית למידע שנאסף באתר וכיו"ב.',
      },
      {
        text: 'המידע הנאסף באתר ומידע שנמסר על ידי הלקוח באופן יזום, לרבות לידים, העדפות, חוזים, פניות, שיחות וכל מידע אחר המועבר בין החברה ללקוח, נשמרים באתר תחת שרת vps מאובטח בחברת צד שלישי ו/או בשרתי האחסון של החברה.',
      },
      {
        text: 'להלן פירוט אודות פעולות אבטחה מסוימות המבוצעות על ידי החברה:',
        sub: [
          'מניעת גישה לכל גורם שאינו בעל הרשאה למערכת הניהול של האתר;',
          'מתן הרשאת ניהול מלאה לבעל האתר וגורמים מטעמו בלבד - שימוש בסיסמאות חזקות בלבד ואימות דו שלבי בהתחברות למערכת הניהול;',
          'מניעת גישה לקוד האתר, בסיס הנתונים, העלאת קבצים שאינם מורשים והתקנת תוספים לאף אחד חוץ מעובדי החברה בעלי גישה מורשית;',
        ],
      },
      {
        text: 'חשוב לזכור שלא ניתן לערוב במאת האחוזים מפני פעילות עוינת ונחושה מצד גורמים זרים ולכן אין בפעולות אלה בטחון מוחלט והאתר לא מתחייב שהשירותים באתר יהיו חסינים באופן מוחלט מפני גישה בלתי מורשית למידע הנאסף בהם אך מתחייב לעשות כל שביכולתו על מנת להגן ולאבטח את המידע.',
      },
    ],
  },
  {
    title: 'זכות גישה למידע הנאסף באתר:',
    clauses: [
      {
        text: 'בהתאם לחוק הגנת הפרטיות, התשמ"א-1981 ("החוק") למשתמשים עומדת הזכות לבקש גישה לנתונים האישיים ולמידע שהאתר אוסף אודותיהם, כמפורט להלן.',
      },
      {
        text: 'זכות עיון במידע (סעיף 13 לחוק הגנת הפרטיות, התשמ"א–1981)',
        sub: [
          'כל אדם זכאי לעיין בעצמו או על ידי בא-כוחו שהוסמך לכך בכתב, או על ידי אפוטרופוסו, במידע שנוגע אליו ומוחזק במאגרי המידע של החברה.',
          'אדם המעוניין לעיין במידע כאמור רשאי לפנות אלינו בבקשה בכתב לכתובת המייל המפורטת שבתחתית מדיניות הפרטיות.',
          'החברה תעמיד את המידע לעיון המבקש בתוך פרק הזמן הקבוע בחוק ותפעל בהתאם להוראותיו.',
        ],
      },
      {
        text: 'זכות תיקון או מחיקה של מידע (סעיף 14 לחוק הגנת הפרטיות, התשמ"א–1981)',
        sub: [
          'מצא אדם כי המידע הנוגע אליו במאגרי המידע של החברה אינו נכון, שלם, ברור או מעודכן – הוא רשאי לפנות אל החברה בבקשה לתקן את המידע או למוחקו.',
          'החברה תבחן את הבקשה ותשיב למבקש בהתאם להוראות החוק. במקרה שתדחה את הבקשה, רשאי המבקש לערער על כך בדרך ובמועדים הקבועים בדין.',
          'הפניות בעניין זה ניתן למסור לכתובת הדוא"ל המפורטת בתחתית מדיניות הפרטיות.',
        ],
      },
      {
        text: 'פנייה בבקשה כאמור לעיל תישלח לאתר באמצעות פרטי הקשר המופיעים בסוף מדיניות הפרטיות.',
      },
      {
        text: 'ככל שתפנה אלינו, נעשה כל מאמץ על מנת לאפשר לך למצות את זכויותיך בהתאם לדין לרבות זכות, העיון, התיקון ומחיקת המידע אודותיך אך מבלי לפגוע מהאמור, אנו נהיה רשאים שלא למסור לך מידע במידה ויתקיימו הנסיבות הקבועות בחוק המאפשרות לנו לא למסור מידע.',
      },
    ],
  },
  {
    title: 'שינויים במדיניות הפרטיות:',
    clauses: [
      {
        text: 'האתר שומר על זכותו לשנות, בכל עת וללא הודעה מוקדמת את מדיניות פרטיות זו ושינויים אלו ייכנסו לתוקפם מיד עם הכנסתם למדיניות הפרטיות.',
      },
      {
        text: 'במקרה של שינוי מהותי במדיניות הפרטיות, האתר יודיע על כך למשתמשים באמצעות פרסום הודעה בולטת באתר. במקרה שהאתר יבקש להחיל שינוי מהותי זה גם על מידע שכבר נאסף באתר, תישלח הודעה למשתמש במטרה לבקש את הסכמתו לכך.',
      },
      {
        text: 'האתר ממליץ למשתמשים לעיין במדיניות פרטיות זו מעת לעת על מנת לקבל מידע על האופן שבו האתר מגן על המידע שלהם.',
      },
    ],
  },
  {
    title: 'צרו קשר:',
    clauses: [
      {
        text: 'האתר מקפיד על קיום הוראות החוק ומכבד את זכותם של משתמשי האתר ואחרים לפרטיות. אם אתה סבור כי פרטיותך נפגעה באופן כלשהו, אנא פנה אלינו לפי הפרטים שלהלן ואנו נשתדל לטפל בפנייתך בכל ההקדם. פניות כאמור ניתן להעביר באמצעים הבאים:',
        notes: [<LegalContact phone={PRIVACY_PHONE} email={PRIVACY_EMAIL} />],
      },
    ],
  },
]

export default function PrivacyPolicyPage() {
  return (
    <LegalDocument
      title="מדיניות פרטיות"
      subtitle='מדיניות פרטיות "יפתח ונגר – יעוץ פיננסי חכם"'
      lastUpdated={LAST_UPDATED}
      preamble={PREAMBLE}
      sections={SECTIONS}
    />
  )
}
